/** @jsxImportSource theme-ui */
import React, { FC, useState } from 'react';
import { Button } from 'semantic-ui-react';
import { ThemeProvider } from 'theme-ui';

import { ApplicationPaths } from '../../auth/AuthorizationConstants';
import { signInRedirect } from '../../auth/AuthorizeService';
import theme from '../../theme';
import { toAbsoluteAuthUrl } from '../../utils/urlHelpers';
import { Typography } from '../_common/Typography';
import InsightsLogo from './InsightsLogo';
import Splash from './Splash';

type LandingPageProps = {
  authUrlMatch?: Array<string>;
};

const LandingPage: FC<LandingPageProps> = ({ authUrlMatch }) => {
  const [isLoading, setIsLoading] = useState(false);

  if (authUrlMatch && new Set(['login-callback', 'logout-callback']).has(authUrlMatch[1])) {
    // For callback in-between pages, don't load anything as there is an immediate redirect
    return <div />;
  }

  if (authUrlMatch) {
    // Replace auth-related urls so users won't see Landing Page with an auth url (e.g. logged-out)
    window.history.replaceState({}, '', '/');
  }

  // Clean up the root in index.html
  document.getElementById('loading-screen')?.remove();
  document.getElementById('navbar')?.remove();
  const root = document.getElementById('root');
  root.style.setProperty('top', '0');
  root.style.setProperty('right', '0');
  root.style.setProperty('pointer-events', 'auto');

  async function login() {
    setIsLoading(true);
    const authRegex = new RegExp(`${ApplicationPaths.AuthorizationPrefix}/(.+)`);
    const isAuth = window.location.pathname.match(authRegex);
    await signInRedirect({ returnUrl: (!isAuth && window.location.href) || '/' });
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <div
          sx={{
            display: 'flex',
            flexDirection: ['column-reverse', 'row'],
            flexWrap: 'nowrap',
            width: '100%',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          {/* Left Section */}
          <div
            sx={{
              minHeight: '25rem',
              flexGrow: 4,
              flexShrink: 1,
              flexBasis: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              minWidth: '400px',
            }}
          >
            {/* Logo */}
            <InsightsLogo
              styles={{
                display: ['none', 'flex'],
                paddingLeft: '2rem',
                paddingTop: '2rem',
                paddingBottom: '2rem',
              }}
            />

            {/* Main Content */}
            <div
              sx={{
                flexGrow: 1,
                padding: ['2.5rem', '3.5rem'],
                marginTop: ['auto', '5rem'],
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'start',
                width: '100%',
              }}
            >
              <Typography variant="h4" color="deepSea90" marginBottom="1.5rem">
                Already an Insights user?
              </Typography>
              <Button className="bd-login-button" disabled={isLoading} onClick={() => login()}>
                Log in
              </Button>
              <Typography
                variant="body1"
                color="deepSea90"
                sx={{
                  marginTop: '1rem',
                }}
              >
                Not an existing user?{' '}
                <a href="https://meet.bluedot.global/request-a-meeting-insights?utm_source=insights_login_page&utm_medium=website&utm_campaign=insights_login_page">
                  Get in touch!
                </a>
              </Typography>
            </div>

            {/* Footer */}
            <div
              sx={{
                width: '100%',
                flexGrow: 0,
                padding: '2rem',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <a href={toAbsoluteAuthUrl('/BlueDot/PrivacyPolicy')}>
                <Typography variant="caption" color="stone50">
                  Privacy Policy
                </Typography>
              </a>
            </div>
          </div>

          {/* Right (Splash) Section */}
          <div
            sx={{
              height: '100%',
              minHeight: '25rem',
              maxHeight: ['50%', '100%'],
              flexGrow: 8,
              flexShrink: 1,
              flexBasis: 0,
            }}
          >
            <Splash />
          </div>
        </div>
      </ThemeProvider>
    </>
  );
};

export default LandingPage;
